import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowUpRight, ClipboardList, X } from "lucide-react";
import { loadFinderProfile, clearFinderProfile } from "../../content/finder";

const API = process.env.REACT_APP_BACKEND_URL;

const INTERESTS = [
  "Rooftop Solar", "Open Access Solar", "Third-Party Open Access", "Captive Solar",
  "Group Captive Solar", "Wind-Solar Hybrid", "Solar Procurement Advisory", "Not sure yet",
];
const TYPES = [
  { v: "consumer", l: "C&I energy consumer" },
  { v: "partner", l: "Developer / EPC / Investor" },
  { v: "general", l: "General enquiry" },
];

const field = "w-full border-0 border-b border-[#082619]/25 bg-transparent px-0 py-3 text-[0.95rem] text-[#082619] placeholder:text-[#4C6053]/70 focus:border-[#74DC26] focus:outline-none focus:ring-0 transition-colors";
const label = "eyebrow !text-[0.65rem] text-[#4C6053]";

const formatErr = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ");
  return "Please check the form and try again.";
};

const Field = ({ id, title, children }) => (
  <label htmlFor={id} className="block">
    <span className={label}>{title}</span>
    {children}
  </label>
);

const FinderSummary = ({ finder, onRemove }) => (
  <div className="sm:col-span-2 rounded-xl border border-[#74DC26]/50 bg-[#F2EFE7] p-5" data-testid="enquiry-finder-summary">
    <div className="flex items-start justify-between gap-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-[#082619]">
        <ClipboardList className="h-4 w-4 text-[#3f8a0f]" /> Your Solution Finder answers will be attached
      </p>
      <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-xs font-medium text-[#4C6053] hover:text-[#082619]" data-testid="enquiry-finder-remove">
        <X className="h-3.5 w-3.5" /> Remove
      </button>
    </div>
    <ul className="mt-3 grid gap-x-6 gap-y-1.5 text-xs text-[#4C6053] sm:grid-cols-2">
      {finder.profile.map((p) => <li key={p.question}><span className="text-[#082619]">{p.answer}</span></li>)}
    </ul>
    {finder.shortlist.length > 0 && (
      <p className="mt-3 text-xs text-[#4C6053]">Shortlist: <span className="font-medium text-[#082619]">{finder.shortlist.join(", ")}</span></p>
    )}
  </div>
);

export const EnquiryForm = ({ defaultType = "consumer", defaultInterest = "" }) => {
  const empty = { name: "", company: "", designation: "", email: "", phone: "", enquiry_type: defaultType, interest: "", location: "", message: "", consent: false };
  const [f, setF] = useState({ ...empty, interest: INTERESTS.includes(defaultInterest) ? defaultInterest : "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [finder, setFinder] = useState(() => loadFinderProfile());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Enquiries are temporarily unavailable. Please call us instead.");
    setBusy(true);
    try {
      await axios.post(`${API}/api/enquiries`, {
        ...f,
        finder_profile: finder?.profile ?? [],
        finder_shortlist: finder?.shortlist ?? [],
      });
      clearFinderProfile();
      setFinder(null);
      setDone(true);
      setF(empty);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="rounded-2xl bg-[#082619] p-10 text-[#F9F8F3]" data-testid="enquiry-success">
        <CheckCircle2 className="h-10 w-10 text-[#74DC26]" strokeWidth={1.4} />
        <h3 className="font-display mt-6 text-3xl font-medium">Thank you. Your enquiry has been received.</h3>
        <p className="mt-4 text-[#B4C7BA]">Our team will review your requirement and get in touch.</p>
        <button type="button" onClick={() => setDone(false)} className="mt-8 text-sm font-semibold text-[#74DC26] underline underline-offset-4" data-testid="enquiry-new">
          Send another enquiry
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="grid gap-x-8 gap-y-7 sm:grid-cols-2" data-testid="contact-enquiry-form">
      <Field id="name" title="Full name *"><input id="name" required minLength={2} value={f.name} onChange={set("name")} className={field} placeholder="Your name" data-testid="enquiry-name-input" /></Field>
      <Field id="company" title="Company *"><input id="company" required value={f.company} onChange={set("company")} className={field} placeholder="Company name" data-testid="enquiry-company-input" /></Field>
      <Field id="designation" title="Designation"><input id="designation" value={f.designation} onChange={set("designation")} className={field} placeholder="e.g. CFO, Plant Head" data-testid="enquiry-designation-input" /></Field>
      <Field id="email" title="Work email *"><input id="email" type="email" required value={f.email} onChange={set("email")} className={field} placeholder="name@company.com" data-testid="enquiry-email-input" /></Field>
      <Field id="phone" title="Phone *"><input id="phone" type="tel" required minLength={6} value={f.phone} onChange={set("phone")} className={field} placeholder="+91" data-testid="enquiry-phone-input" /></Field>
      <Field id="location" title="Facility location"><input id="location" value={f.location} onChange={set("location")} className={field} placeholder="City, State" data-testid="enquiry-location-input" /></Field>
      <Field id="enquiry_type" title="I am a">
        <select id="enquiry_type" value={f.enquiry_type} onChange={set("enquiry_type")} className={field} data-testid="enquiry-type-select">
          {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </Field>
      <Field id="interest" title="Area of interest">
        <select id="interest" value={f.interest} onChange={set("interest")} className={field} data-testid="enquiry-interest-select">
          <option value="">Select an option</option>
          {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field id="message" title="Your requirement *">
          <textarea id="message" required minLength={5} rows={4} value={f.message} onChange={set("message")} className={`${field} resize-none`} placeholder="Approximate monthly consumption, sanctioned load, site details or partnership interest" data-testid="enquiry-message-input" />
        </Field>
      </div>
      {finder && <FinderSummary finder={finder} onRemove={() => { clearFinderProfile(); setFinder(null); }} />}
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#4C6053]">
        <input type="checkbox" required checked={f.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-[#082619]" data-testid="enquiry-consent-checkbox" />
        I agree that Solitaire India may contact me about this enquiry and store the details I have shared for that purpose.
      </label>
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy} data-testid="contact-form-submit-button" className="group inline-flex items-center gap-3 rounded-full bg-[#082619] py-2 pl-7 pr-2 text-sm font-semibold text-[#F9F8F3] transition-colors hover:bg-[#12422c] disabled:opacity-60">
          {busy ? "Sending..." : "Submit Enquiry"}
          <span className="grid h-10 w-10 place-items-center rounded-full bg-[#74DC26] text-[#082619] transition-transform duration-500 group-hover:rotate-45">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
          </span>
        </button>
      </div>
    </form>
  );
};
